import { useState } from "react";
import { ChevronDown } from "lucide-react";
import type { AdminSubscriptionItem } from "@/types/subscription";
import type { SubscriptionAction } from "./subscription-columns";
import { SubscriptionCycle } from "@/types/enums";
import { PlanBadge } from "./plan-badge";
import { StatusBadge } from "./status-badge";
import { PlatformBadge } from "./platform-badge";
import { SubscriptionActions } from "./subscription-actions";
import { formatPriceWithCycle, formatNextPayment } from "@/lib/format";
import { cn } from "@/lib/utils";

interface SubscriptionCardProps {
  subscription: AdminSubscriptionItem;
  onAction: (subscription: AdminSubscriptionItem, action: SubscriptionAction) => void;
}

export function SubscriptionCard({
  subscription,
  onAction,
}: SubscriptionCardProps) {
  const [expanded, setExpanded] = useState(false);

  const { text: nextPaymentText, isOverdue } = formatNextPayment(
    subscription.nextPaymentDate
  );
  const cycleText =
    subscription.subscriptionCycle === SubscriptionCycle.MONTHLY
      ? "월간"
      : "연간";

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3 has-[[data-state=open]]:bg-muted/50 transition-colors duration-75">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <PlanBadge plan={subscription.plan} />
          <StatusBadge status={subscription.status} />
        </div>
        <SubscriptionActions
          subscription={subscription}
          onAction={onAction}
        />
      </div>

      <div>
        <p className="text-base font-medium truncate" title={subscription.userEmail}>
          {subscription.userEmail}
        </p>
        <p className="text-sm text-muted-foreground">{subscription.userName}</p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <p className="text-muted-foreground text-xs">가격</p>
          <p className="text-sm font-mono">
            {formatPriceWithCycle(subscription.price, subscription.subscriptionCycle)}
          </p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">주기</p>
          <p className="text-sm">{cycleText}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">플랫폼</p>
          {subscription.platform ? (
            <PlatformBadge platform={subscription.platform} />
          ) : (
            <p className="text-sm text-muted-foreground">-</p>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex items-center gap-1 text-base text-muted-foreground min-h-[44px] w-full"
        aria-expanded={expanded}
        aria-label="상세 정보 토글"
      >
        <ChevronDown
          className={cn(
            "h-4 w-4 transition-transform",
            expanded && "rotate-180"
          )}
        />
        상세 정보
      </button>

      {expanded && (
        <div className="rounded-md bg-muted/50 p-3 space-y-2.5 text-sm leading-relaxed">
          <div className="flex justify-between">
            <span className="text-muted-foreground">다음 결제일</span>
            <span className={isOverdue ? "text-destructive font-medium" : ""}>
              {nextPaymentText}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">월간 크레딧</span>
            <span className="font-mono">
              {subscription.userCredits.monthly.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">구매 크레딧</span>
            <span className="font-mono">
              {subscription.userCredits.purchased.toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
